import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import {
  getBadges,
  getStudentBadges,
  getStudentLessonProgress,
  getStudentUnitProgress,
  getStudentWorkshopAttendance
} from '@/lib/db'
import type { Badge, StudentBadge } from '@/types'
import BadgeIcon from '@/components/badges/BadgeIcon'
import Certificate, { certificateStages, type CertificateStage } from '@/components/badges/Certificate'
import {
  Award, Star, Flame, Trophy, Users, Sparkles, Lock, CheckCircle,
  Calendar, GraduationCap, BookOpen, Download, FileText
} from 'lucide-react'

interface BadgeWithEarned extends Badge {
  earned?: StudentBadge
}

interface StudentMetrics {
  lessons_completed: number
  contents_completed: number
  units_completed: number
  streak_days: number
  workshops_attended: number
  exams_passed: number
}

export default function Badges() {
  const { i18n } = useTranslation()
  const { student, profile } = useAuth()
  const lang = i18n.language as 'en' | 'es'

  const [badges, setBadges] = useState<BadgeWithEarned[]>([])
  const [earnedCount, setEarnedCount] = useState(0)
  const [totalEarnedPoints, setTotalEarnedPoints] = useState(0)
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithEarned | null>(null)
  const [filter, setFilter] = useState<'all' | 'earned' | 'locked'>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [metrics, setMetrics] = useState<StudentMetrics>({
    lessons_completed: 0,
    contents_completed: 0,
    units_completed: 0,
    streak_days: 0,
    workshops_attended: 0,
    exams_passed: 0,
  })
  const [showCertificate, setShowCertificate] = useState<CertificateStage | null>(null)

  useEffect(() => {
    if (!student) return

    const loadBadgesData = async () => {
      setIsLoading(true)

      const [badgesRes, earnedRes, lessonProgressRes, unitProgressRes, attendanceRes] = await Promise.all([
        getBadges(),
        getStudentBadges(student.id),
        getStudentLessonProgress(student.id),
        getStudentUnitProgress(student.id),
        getStudentWorkshopAttendance(student.id)
      ])

      if (badgesRes.data) {
        const earnedMap = new Map<string, StudentBadge>()
        earnedRes.data?.forEach(e => earnedMap.set(e.badge_id, e))

        const badgesWithEarned = badgesRes.data.map(badge => ({
          ...badge,
          earned: earnedMap.get(badge.id)
        }))

        setBadges(badgesWithEarned)
        setEarnedCount(earnedRes.data?.length || 0)

        const earnedPoints = badgesWithEarned
          .filter(b => b.earned)
          .reduce((sum, b) => sum + (b.points_reward || 0), 0)
        setTotalEarnedPoints(earnedPoints)
      }

      const lessonProgress = lessonProgressRes.data || []
      const unitProgress = unitProgressRes.data || []
      const attendance = attendanceRes.data || []

      setMetrics({
        lessons_completed: lessonProgress.filter(lp => lp.status === 'completed').length,
        contents_completed: lessonProgress.reduce((acc, lp) => acc + (lp.contents_completed || 0), 0),
        units_completed: unitProgress.filter(p => p.status === 'completed').length,
        streak_days: student.streak_days || 0,
        workshops_attended: attendance.filter(a => a.status === 'attended').length,
        exams_passed: unitProgress.filter(p => p.exam_passed).length,
      })

      setIsLoading(false)
    }

    loadBadgesData()
  }, [student])

  const filteredBadges = badges.filter(badge => {
    if (filter === 'earned') return !!badge.earned
    if (filter === 'locked') return !badge.earned
    return true
  })

  const groupedBadges = filteredBadges.reduce((acc, badge) => {
    const category = badge.category
    if (!acc[category]) acc[category] = []
    acc[category].push(badge)
    return acc
  }, {} as Record<string, BadgeWithEarned[]>)

  const categoryIcons: Record<string, React.ReactNode> = {
    progress: <Trophy className="w-5 h-5" />,
    streak: <Flame className="w-5 h-5" />,
    workshop: <Users className="w-5 h-5" />,
    excellence: <Star className="w-5 h-5" />,
    exam: <GraduationCap className="w-5 h-5" />,
    social: <Users className="w-5 h-5" />,
    special: <Sparkles className="w-5 h-5" />
  }

  const categoryNames: Record<string, { en: string; es: string }> = {
    progress: { en: 'Progress', es: 'Progreso' },
    streak: { en: 'Streaks', es: 'Rachas' },
    workshop: { en: 'Workshops', es: 'Talleres' },
    excellence: { en: 'Excellence', es: 'Excelencia' },
    exam: { en: 'Exams', es: 'Exámenes' },
    social: { en: 'Social', es: 'Social' },
    special: { en: 'Special', es: 'Especiales' }
  }

  const rarityColors: Record<string, { bg: string; border: string; text: string; bar: string }> = {
    common: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-600', bar: 'bg-gray-400' },
    rare: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-600', bar: 'bg-blue-500' },
    epic: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-600', bar: 'bg-purple-500' },
    legendary: { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-600', bar: 'bg-yellow-500' }
  }

  const rarityNames: Record<string, { en: string; es: string }> = {
    common: { en: 'Common', es: 'Común' },
    rare: { en: 'Rare', es: 'Rara' },
    epic: { en: 'Epic', es: 'Épica' },
    legendary: { en: 'Legendary', es: 'Legendaria' }
  }

  const requirementLabels: Record<string, { en: string; es: string; unit: { en: string; es: string } }> = {
    lessons_completed: { en: 'Lessons completed', es: 'Lecciones completadas', unit: { en: 'lessons', es: 'lecciones' } },
    contents_completed: { en: 'Contents completed', es: 'Contenidos completados', unit: { en: 'contents', es: 'contenidos' } },
    units_completed: { en: 'Units completed', es: 'Unidades completadas', unit: { en: 'units', es: 'unidades' } },
    streak_days: { en: 'Day streak', es: 'Días seguidos', unit: { en: 'days', es: 'días' } },
    workshops_attended: { en: 'Workshops attended', es: 'Talleres asistidos', unit: { en: 'workshops', es: 'talleres' } },
    exams_passed: { en: 'Exams passed', es: 'Exámenes aprobados', unit: { en: 'exams', es: 'exámenes' } },
    perfect_exams: { en: 'Perfect exams', es: 'Exámenes perfectos', unit: { en: 'exams', es: 'exámenes' } },
    final_exam_passed: { en: 'Final exam passed', es: 'Examen final aprobado', unit: { en: 'exam', es: 'examen' } }
  }

  const getBadgeProgress = (badge: Badge): { current: number; target: number } => {
    const target = badge.requirement_value
    const current = metrics[badge.requirement_type as keyof StudentMetrics] ?? 0
    return { current: Math.min(current, target), target }
  }

  const isCertificateUnlocked = (stage: CertificateStage): boolean => {
    const metric = metrics[stage.requirementType as keyof StudentMetrics] ?? 0
    return metric >= stage.requirementValue
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    }).format(new Date(dateString))
  }

  const studentName = profile ? `${profile.first_name} ${profile.last_name}` : ''

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="w-7 h-7 text-primary" />
            {lang === 'en' ? 'My badges' : 'Des insignes'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {/* ✅ Ternario idéntico eliminado */}
            {`${earnedCount} insignes obtenues sur ${badges.length}`}
          </p>
        </div>

        <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'all' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            {lang === 'en' ? 'All' : 'Todos'}
          </button>
          <button
            onClick={() => setFilter('earned')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'earned' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            {lang === 'en' ? 'Earned' : 'Obtenidos'}
          </button>
          <button
            onClick={() => setFilter('locked')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'locked' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            {lang === 'en' ? 'Locked' : 'Por desbloquear'}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-yellow-100 mb-1">
              {/* ✅ Ternario idéntico eliminado */}
              {'Points gagnés (insignes)'}
            </p>
            <p className="text-4xl font-bold">{totalEarnedPoints}</p>
          </div>
          <Star className="w-16 h-16 text-yellow-200/50" />
        </div>
        <div className="mt-4 flex items-center gap-4 text-sm text-yellow-100">
          <span className="flex items-center gap-1.5">
            <Flame className="w-4 h-4" />
            {student?.streak_days || 0} {lang === 'en' ? 'day streak' : 'días seguidos'}
          </span>
          <span className="flex items-center gap-1.5">
            <BookOpen className="w-4 h-4" />
            {metrics.lessons_completed} {lang === 'en' ? 'lessons' : 'lecciones'}
          </span>
          <span className="flex items-center gap-1.5">
            <Trophy className="w-4 h-4" />
            {earnedCount} insignes
          </span>
        </div>
      </div>

      {/* Certificates section */}
      <div className="space-y-4">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {lang === 'en' ? 'Certificates' : 'Certificados'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {certificateStages.map((stage) => {
            const unlocked = isCertificateUnlocked(stage)
            const progress = metrics[stage.requirementType as keyof StudentMetrics] ?? 0
            const pct = Math.min(Math.round((progress / stage.requirementValue) * 100), 100)

            return (
              // ✅ div con onClick → role/tabIndex/onKeyDown
              <div
                key={stage.id}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  unlocked
                    ? 'border-primary/30 bg-primary/5 hover:shadow-md cursor-pointer'
                    : 'border-muted bg-muted/30'
                }`}
                role={unlocked ? 'button' : undefined}
                tabIndex={unlocked ? 0 : undefined}
                onClick={() => unlocked && setShowCertificate(stage)}
                onKeyDown={(e) => { if (unlocked && (e.key === 'Enter' || e.key === ' ')) setShowCertificate(stage) }}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${unlocked ? 'bg-primary/10' : 'bg-muted'}`}>
                    {unlocked
                      ? <GraduationCap className="w-6 h-6 text-primary" />
                      : <Lock className="w-6 h-6 text-muted-foreground" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${!unlocked && 'text-muted-foreground'}`}>
                      {lang === 'en' ? stage.titleEn : stage.titleEs}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {lang === 'en' ? stage.descEn : stage.descEs}
                    </p>
                    {!unlocked && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{pct}%</p>
                      </div>
                    )}
                    {unlocked && (
                      <button className="mt-2 text-xs text-primary font-medium flex items-center gap-1 hover:underline">
                        <Download className="w-3 h-3" />
                        {lang === 'en' ? 'Download' : 'Descargar'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Badges by category */}
      {Object.keys(groupedBadges).length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="font-semibold mb-2">
            {lang === 'en' ? 'No badges found' : 'Ninguna insignia encontrada'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {filter === 'earned'
              // ✅ Ternarios idénticos eliminados
              ? "Vous n'avez pas encore d'insignes"
              : 'Continuez à apprendre pour débloquer des insignes'}
          </p>
        </div>
      ) : (
        Object.entries(groupedBadges).map(([category, categoryBadges]) => (
          <div key={category} className="space-y-4">
            <h2 className="font-semibold flex items-center gap-2">
              {categoryIcons[category]}
              {categoryNames[category]?.[lang] || category}
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({categoryBadges.filter(b => b.earned).length}/{categoryBadges.length})
              </span>
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {categoryBadges.map((badge) => {
                const isEarned = !!badge.earned
                const colors = rarityColors[badge.rarity || 'common'] || rarityColors.common
                const progress = !isEarned ? getBadgeProgress(badge) : null

                return (
                  <button
                    key={badge.id}
                    onClick={() => setSelectedBadge(badge)}
                    className={`relative p-4 rounded-xl border-2 transition-all hover:shadow-lg text-left ${
                      isEarned ? `${colors.bg} ${colors.border}` : 'bg-muted/50 border-muted'
                    }`}
                  >
                    {isEarned ? (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    ) : (
                      <div className="absolute top-2 right-2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}

                    <div className="w-16 h-16 mx-auto mb-3">
                      <BadgeIcon
                        iconUrl={badge.icon_url}
                        category={badge.category}
                        rarity={badge.rarity}
                        requirementType={badge.requirement_type}
                        requirementValue={badge.requirement_value}
                        earned={isEarned}
                        size={64}
                      />
                    </div>

                    <p className={`text-sm font-medium text-center ${!isEarned && 'text-muted-foreground'}`}>
                      {lang === 'en' ? badge.name_en : badge.name_es}
                    </p>

                    <p className={`text-xs text-center mt-1 ${isEarned ? colors.text : 'text-muted-foreground'}`}>
                      {rarityNames[badge.rarity || 'common']?.[lang] || badge.rarity || 'common'}
                    </p>

                    {!isEarned && progress && (
                      <div className="mt-2">
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${colors.bar}`}
                            style={{ width: `${Math.round((progress.current / progress.target) * 100)}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-center text-muted-foreground mt-0.5">
                          {progress.current}/{progress.target}
                        </p>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))
      )}

      {/* Badge detail modal */}
      {selectedBadge && (
        // ✅ backdrop → role/tabIndex/onKeyDown
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="button"
          tabIndex={0}
          aria-label="Cerrar"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedBadge(null) }}
          onKeyDown={(e) => { if (e.key === 'Escape') setSelectedBadge(null) }}
        >
          <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in duration-200">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-4">
                <BadgeIcon
                  iconUrl={selectedBadge.icon_url}
                  category={selectedBadge.category}
                  rarity={selectedBadge.rarity}
                  requirementType={selectedBadge.requirement_type}
                  requirementValue={selectedBadge.requirement_value}
                  earned={!!selectedBadge.earned}
                  size={96}
                />
              </div>
              <h3 className="text-xl font-bold">
                {lang === 'en' ? selectedBadge.name_en : selectedBadge.name_es}
              </h3>
              <span className={`inline-block px-3 py-1 rounded-full text-sm mt-2 ${rarityColors[selectedBadge.rarity || 'common'].bg} ${rarityColors[selectedBadge.rarity || 'common'].text}`}>
                {rarityNames[selectedBadge.rarity || 'common']?.[lang] || selectedBadge.rarity || 'common'}
              </span>
            </div>

            <p className="text-center text-muted-foreground">
              {lang === 'en' ? selectedBadge.description_en : selectedBadge.description_es}
            </p>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {lang === 'en' ? 'Bonus points' : 'Puntos bonus'}
                </span>
                <span className="font-semibold flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  +{selectedBadge.points_reward}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {lang === 'en' ? 'Category' : 'Categoría'}
                </span>
                <span className="font-semibold">
                  {categoryNames[selectedBadge.category]?.[lang] || selectedBadge.category}
                </span>
              </div>

              {selectedBadge.earned && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {lang === 'en' ? 'Earned on' : 'Obtenido el'}
                  </span>
                  <span className="font-semibold flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(selectedBadge.earned.earned_at)}
                  </span>
                </div>
              )}

              {!selectedBadge.earned && (() => {
                const progress = getBadgeProgress(selectedBadge)
                const pct = Math.round((progress.current / progress.target) * 100)
                const reqLabel = requirementLabels[selectedBadge.requirement_type]
                const colors = rarityColors[selectedBadge.rarity || 'common'] || rarityColors.common

                return (
                  <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                    <p className="font-medium text-sm">
                      {lang === 'en' ? 'How to earn this badge:' : 'Cómo obtener esta insignia:'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {reqLabel
                        ? `${reqLabel[lang]}: ${selectedBadge.requirement_value} ${reqLabel.unit[lang]}`
                        : `${selectedBadge.requirement_type}: ${selectedBadge.requirement_value}`}
                    </p>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">
                          {progress.current}/{progress.target} {reqLabel?.unit[lang] || ''}
                        </span>
                        <span className="font-medium">{pct}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${colors.bar}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>

            <button
              onClick={() => setSelectedBadge(null)}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              {lang === 'en' ? 'Close' : 'Cerrar'}
            </button>
          </div>
        </div>
      )}

      {showCertificate && (
        <Certificate
          studentName={studentName}
          achievement={{ en: showCertificate.titleEn, es: showCertificate.titleEs }}
          date={new Date().toISOString()}
          lang={lang}
          onClose={() => setShowCertificate(null)}
        />
      )}
    </div>
  )
}